import { fg } from '@atlaskit/platform-feature-flags';

import { isAdfStreamingEnabled } from '../utils/adf-streaming';
import type { ConvoAiFeatureId } from '../utils/request/types';

// Note -- when updating this function -- also update
// the one used in packages/editor/editor-plugin-ai/examples/utils/getDevFetchCustomHeaders.ts
export function _getFetchCustomHeaders(): HeadersInit {
	const currentHost = window.location.origin;

	switch (true) {
		case currentHost === 'https://statlas.prod.atl-paas.net':
			// Update this to add custom headers when run in atlaskit branch deploys
			// ie.
			// {
			//   'X-CloudID': 'Magic string'
			// }
			return {};
		default:
			return {};
	}
}

export function deriveHost() {
	const {
		origin = '',
		port = '',
		hostname = '',
	} = typeof window.location !== 'undefined' ? window.location : {};

	if (
		(hostname === 'localhost' && port !== '8081') ||
		[
			'atlaskit-website-staging.stg-east.frontend.public.atl-paas.net',
			'statlas.prod.atl-paas.net',
			'atlaskit.atlassian.com',
		].some((h) => hostname === h)
	) {
		return 'https://pug.jira-dev.com';
	}

	return origin;
}

export const convoAIStatelessApiURL = `${deriveHost()}/gateway/api/assist/chat/v1/invoke_agent/stream`;
export const convoAIApiUrl = `${deriveHost()}/gateway/api/assist/api/ai/v2/ai-feature/editor-ai/stream`;
export const convoAIRovoApiURL = `${deriveHost()}/gateway/api/assist/rovo/v1/editor/conversation`; // + `/${channelID}/message/stream` at the end;
export const convoAIAuditLogApiURL = `${deriveHost()}/gateway/api/ai/v2/ai-feature/audit-logs`;
export const jiraIssueReformatterApiURL = `${deriveHost()}/gateway/api/assist/ai/v2/ai-feature/jira/issue/source-type/work-item/suggestions/stream`;

export function deriveProactiveAIApiUrl() {
	return `${deriveHost()}/gateway/api/assist/ai/v2/ai-feature/proactive`;
}

export const getAPIName = (endpoint: string): string => {
	switch (endpoint) {
		case convoAIStatelessApiURL:
			return 'assistance-service';
		case convoAIApiUrl:
			return 'convo-ai';
		case convoAIRovoApiURL:
			return 'rovo-agent';
		case convoAIAuditLogApiURL:
			return 'audit-logs';
		case jiraIssueReformatterApiURL:
			return 'jira-issue-reformatter';
		case deriveProactiveAIApiUrl():
			return 'proactive-ai';
		default:
			return 'unknown-api';
	}
};

/**
 * This is part 1 of intent schema IDs that will be migrated to the Convo AI endpoint and will be
 * enabled when the feature flag `editor_ai_convo_ai_endpoint_202505` is enabled.
 */
const convoAIMigratedIntentsPart1 = new Set<ConvoAiFeatureId>([
	'find_action_items',
	'change_tone_casual',
	'change_tone_educational',
	'change_tone_empathetic',
	'change_tone_neutral',
	'change_tone_professional',
	'fix_spelling_grammar',
	'improve_writing',
	'make_longer',
	'rephrase',
	'make_shorter',
	'suggest_title',
	'summarize_writing',
	'translate',
	'atlas_make_shorter',
]);

/**
 * This is part 2 of intent schema IDs that will be migrated to the Convo AI endpoint and will be
 * enabled when the feature flag `platform_editor_ai_convo_ai_migration_part2` is enabled.
 */
const convoAIMigratedIntentsPart2 = new Set<ConvoAiFeatureId>([
	'bitbucket_pr_description',
	'brainstorm',
	'free_generate',
	'interrogation',
]);

/**
 * There are new ADF intents that are only available on the new Convo AI endpoint.
 */
const adfIntents = new Set<ConvoAiFeatureId>([
	'improve_writing_adf_gemini25flash',
	'improve_writing_adf_gemini20flash',
	'improve_writing_adf_gpt4mini',
	'improve_writing_adf_gpt41mini',
	'improve_writing_adf_gpt5mini',
	'improve_writing_adf_v103',
	'change_tone_casual_adf',
	'change_tone_casual_adf_gpt41mini',
	'change_tone_educational_adf',
	'change_tone_educational_adf_gpt41mini',
	'change_tone_empathetic_adf',
	'change_tone_empathetic_adf_gpt41mini',
	'change_tone_neutral_adf',
	'change_tone_professional_adf',
	'change_tone_professional_adf_gpt41mini',
	'change_tone_casual_adf_v11',
	'change_tone_casual_adf_v111',
	'change_tone_empathetic_adf_v11',
	'change_tone_empathetic_adf_v111',
	'change_tone_professional_adf_v11',
	'change_tone_professional_adf_v111',
	'fix_spelling_grammar_adf',
	'fix_spelling_grammar_adf_gpt41mini',
	'add_polish_adf',
	'make_longer_adf',
	'make_shorter_adf',
	'make_longer_adf_gpt41mini',
	'make_shorter_adf_gpt41mini',
	'make_longer_adf_v11',
	'make_longer_adf_v111',
	'make_shorter_adf_v11',
	'make_shorter_adf_v111',
	'translate_adf',
	'translate_adf_gpt41mini',
]);

export const isTargetingNewConvoAIEndpoint = (featureId?: ConvoAiFeatureId) => {
	if (!featureId) {
		return false;
	}
	const adfStreamingEnabled = isAdfStreamingEnabled();
	return (
		(adfIntents.has(featureId) && adfStreamingEnabled) ||
		(convoAIMigratedIntentsPart1.has(featureId) && fg('editor_ai_convo_ai_endpoint_202505')) ||
		(convoAIMigratedIntentsPart2.has(featureId) &&
			fg('platform_editor_ai_convo_ai_migration_part2'))
	);
};
